import { OnInit, Output, Input, EventEmitter, Injector } from '@angular/core';
import { DataGridTemplate } from '../../../../shared/data-grid/data-grid.template';
import { FormGroup, FormBuilder } from '@angular/forms';
import { PartService } from '../../../phf/phf.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { CgpAlertDialogService } from '../../../../shared/cgp-alert-dialog/cgp-alert-dialog.service';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { UploadService } from './upload-service';
import { InvalidColumnsGrid } from './invalid-columns-grid';
import { FilterDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';

export class ThroughputGrid extends DataGridTemplate {
  private readonly formBuilder: FormBuilder;
  private readonly partService: PartService;
  private readonly alertDialog: CgpAlertDialogService;
  private readonly uploadService: UploadService;
  filter: FilterDescriptor;
  invalidFilterClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  validFilterClick: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  data: any[];
  editForm: FormGroup;
  gridColumns: any;
  constructor(private readonly injector: Injector) {
    super();
    this.controls.excel = false;
    this.controls.add = false;
    this.controls.edit = false;
    this.controls.delete = false;
    this.controls.upload = true;
    this.formBuilder = injector.get(FormBuilder);
    this.partService = injector.get(PartService);
    this.alertDialog = injector.get(CgpAlertDialogService);
    this.uploadService = injector.get(UploadService);
    this.controls.customToolBarButtons = this.throughputGridCustomToolBarButtons;
    this.setAllowedUploadExtensions(['.xlsx']);
    this.detailTemplate = new InvalidColumnsGrid(injector);
    this.editForm = this.formBuilder.group({});

  }

  // Watch changes on grid and emit the grid's content to the modal

  /**
   * Reads the processed payload and inputs this into the grid.
   * @param opts 
   */
  read(opts: any): Promise<any> {
    this.data = [];
    if (this.uploadFiles.length > 0) {
      const file: File = this.uploadFiles[0].rawFile;
      const formData: FormData = new FormData();
      formData.append('file', file);

      return this.partService.uploadExcel(formData, true, '/UploadThroughput', true)
        .then(response => this.mapThroughputResponse(response))
    }
    else {
      return Promise.resolve([]);
    }
  }


  private mapThroughputResponse(response) {
    if (response) {
      response.forEach(row => {
        if (row) {
          const newRow = {
            RowIndex: row.RowIndex,
            ResponseStatus: row.ResponseStatus,
            ColumnDtos: {},
            InvalidColumns: [],
            TempColumns: []
          }

          newRow.TempColumns = row.ColumnDtos;
          row.ColumnDtos.forEach(column => {
            newRow.ColumnDtos[column.ColumnName] = column.ColumnValue;

            if (column.ErrorMessage != null) {
              newRow.InvalidColumns.push({ [column.ColumnName]: column.ColumnValue, ErrorMessage: column.ErrorMessage })
            }
          });
          this.data.push(newRow);
        }
      });
    }
    this.gridColumns = this.data;
    return this.data;
  }


  create(parentEditDataItem: any): void { }

  edit(dataItem: any): void { }

  save(dataItem: any, isNew: boolean): Promise<any> {
    return Promise.resolve([]);
  }

  handleError = (error: Response) => {
    const _body = error.json();
    this.alertDialog.showAlertDialog({
      title: 'Upload Error: ',
      message: _body,
      alert: 'danger',
      closeLabel: 'Close',
    });
  }

  delete(dataItem: any): Promise<any> {
    return Promise.resolve([]);
  }

  /**
   * 
   * @param context 
   * callback function to add 'highlight' html classs to a row
  */
  rowCallback = (context: RowClassArgs) => {
    const invalid = context.dataItem.ResponseStatus === 'INVALID';
    return {
      highlight: invalid,
    };

  }

  handleApply = () => {
    this.uploadService.showApplyReport.next(true);
  }



  filterInvalid = () => {
    if (this.invalidFilterClick.value === false) {
      this.filter = {
        operator: 'eq',
        field: 'ResponseStatus',
        value: 'INVALID'
      };

      const filterArray = [this.filter];

      const compositDescriptor: CompositeFilterDescriptor = {
        logic: 'or',
        filters: filterArray,
      }
      this.gridState.filter = compositDescriptor;
      this.gridState.filter.filters = [this.filter];
      this.setGridData();
      this.invalidFilterClick.next(true);
      this.validFilterClick.next(false);
    }
    else {
      this.gridState.filter = undefined;
      this.setGridData();
      this.invalidFilterClick.next(false);
    }
  }

  filterValid = () => {
    if (this.validFilterClick.value === false) {
      this.filter = {
        operator: 'eq',
        field: 'ResponseStatus',
        value: 'VALID'
      };

      const filterArray = [this.filter];

      const compositDescriptor: CompositeFilterDescriptor = {
        logic: 'or',
        filters: filterArray,
      }
      this.gridState.filter = compositDescriptor;
      this.gridState.filter.filters = [this.filter];
      this.setGridData();
      this.validFilterClick.next(true);
      this.invalidFilterClick.next(false);
    }
    else {
      this.gridState.filter = undefined;
      this.setGridData();
      this.validFilterClick.next(false);
    }
  }

  /**
   * condition whether or not to show the detail for a row
   */
  showGridDetailCondition = (dataItem: any) => {
    return dataItem.ResponseStatus === 'INVALID';
  };

  isDisabled = (id: string): boolean => {
    const allInvalidData = this.data.some(x => x.ResponseStatus === "INVALID") && !this.data.some(x => x.ResponseStatus === "VALID");
    const emptyData = this.data.length === 0;
    return id === "apply" && (emptyData || allInvalidData);
  }

  columns: any[] = [
    {
      field: 'ColumnDtos.STAMPING PLANT',
      title: 'Stamping Plant',
    },
    {
      field: 'ColumnDtos.PRESS CLASS',
      title: 'Press Class',
    },
    {
      field: 'ColumnDtos.TPD EQUIP TYPE',
      title: 'TPD Equip Type',
    },
    {
      field: 'ColumnDtos.LINE',
      title: 'Line',
    },
    {
      field: 'ColumnDtos.CSPC',
      title: 'CSPC',
    },
    {
      field: "ColumnDtos.NSPH",
      title: "NSPH",
    },
    {
      field: "ColumnDtos.AVG SPEED STROKES PER MIN",
      title: "Avg. Speed strokes per Min",
    },
  ]

  throughputGridCustomToolBarButtons = [
    {
      group: true,
      buttons: [{
        id: 'filterInvalid',
        title: 'Filter Invalid',
        text: 'Filter Invalid',
        iconClass: 'fa fa-w fa-filter',
        callback: this.filterInvalid,
        togglable: true,
        //disabled: this.isDisabled('filterInvalid'),

        isSelected: (): boolean => this.invalidFilterClick.value,
      },
      {
        id: 'filterValid',
        title: 'Filter Valid',
        text: 'Filter valid',
        iconClass: 'fa fa-w fa-filter',
        callback: this.filterValid,
        togglable: true,
        //disabled: false,
        isSelected: (): boolean => this.validFilterClick.value
      }]
    },
    {
      id: 'apply',
      title: 'Apply',
      text: 'Submit',
      iconClass: 'fa fa-w fa-check',
      callback: this.handleApply,
      togglable: true,
      //disabled: false,
      //isSelected: (): boolean => this.icrClick.value
    },

  ]
}
